import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { getUser, setAuth, getToken } from "../lib/auth";
import { toast } from "sonner";
import { User, MessageSquare, Bookmark, Mail, KeyRound, Save, Loader2, Trash2, CheckCircle2, Shield, Tags, Check, Plus } from "lucide-react";

const TOPICS = ["AI", "Robotics", "LLM", "Software"];

export default function Profile() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [comments, setComments] = useState([]);
  const [name, setName] = useState("");
  const [pw, setPw] = useState({ current: "", next: "", confirm: "" });
  const [busy, setBusy] = useState(false);

  const load = () => {
    api.get("/profile/overview").then((r) => {
      setData(r.data);
      setName(r.data.user.name || "");
    }).catch(() => navigate("/login"));
    api.get("/profile/comments").then((r) => setComments(r.data)).catch(() => {});
  };

  useEffect(() => {
    if (!getUser()) {
      navigate("/login");
      return;
    }
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const saveName = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const r = await api.put("/profile", { name });
      setAuth(getToken(), { ...getUser(), name: r.data.name });
      toast.success("Nama diperbarui");
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Gagal menyimpan");
    }
    setBusy(false);
  };

  const changePw = async (e) => {
    e.preventDefault();
    if (pw.next !== pw.confirm) {
      toast.error("Konfirmasi password tidak cocok");
      return;
    }
    setBusy(true);
    try {
      await api.post("/profile/change-password", { current_password: pw.current, new_password: pw.next });
      toast.success("Password berhasil diubah");
      setPw({ current: "", next: "", confirm: "" });
    } catch (err) {
      toast.error(err.response?.data?.detail || "Gagal mengubah password");
    }
    setBusy(false);
  };

  const deleteComment = async (id) => {
    if (!window.confirm("Hapus komentar ini?")) return;
    try {
      await api.delete(`/comments/${id}`);
      setComments(comments.filter((c) => c.id !== id));
      toast.success("Komentar dihapus");
    } catch {
      toast.error("Gagal menghapus");
    }
  };

  const toggleEmails = async (receive) => {
    try {
      const r = await api.put("/newsletter/email-preference", { receive_emails: receive });
      setData({ ...data, subscription: { ...data.subscription, ...r.data } });
      toast.success(receive ? "Email newsletter diaktifkan" : "Anda tidak akan menerima email newsletter");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Gagal menyimpan preferensi");
    }
  };

  const toggleTopic = async (topic) => {
    const current = data.subscription.topics || [];
    const next = current.includes(topic) ? current.filter((t) => t !== topic) : [...current, topic];
    if (next.length === 0) {
      toast.error("Pilih minimal satu topik");
      return;
    }
    try {
      const r = await api.put("/newsletter/email-preference", { topics: next });
      setData({ ...data, subscription: { ...data.subscription, ...r.data } });
      toast.success(`Topik email: ${r.data.topics.join(", ")}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Gagal menyimpan topik");
    }
  };

  if (!data) return <div className="text-center py-32 text-slate-500">Memuat profil...</div>;
  const u = data.user;
  const isGoogle = u.auth_provider === "google";

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-12 relative z-10" data-testid="profile-page">
      <div className="flex items-center gap-4 mb-10">
        {u.picture ? (
          <img src={u.picture} alt="" className="w-16 h-16 rounded-2xl" />
        ) : (
          <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-cyan1 to-magenta1 flex items-center justify-center font-heading text-2xl font-black text-void">
            {(u.name || u.email)[0].toUpperCase()}
          </div>
        )}
        <div>
          <h1 className="font-heading text-3xl md:text-4xl font-black" data-testid="profile-name">{u.name || u.email}</h1>
          <div className="flex items-center gap-2 mt-1 text-sm text-slate-500 font-mono flex-wrap">
            <span>{u.email}</span>
            <span className="chip text-[10px]">{isGoogle ? "GOOGLE" : "EMAIL"}</span>
            {u.role === "admin" && <span className="chip chip-magenta text-[10px]"><Shield className="w-3 h-3" /> ADMIN</span>}
          </div>
        </div>
      </div>

      <div className="grid sm:grid-cols-3 gap-4 mb-10">
        <div className="card p-5" data-testid="stat-comments">
          <MessageSquare className="w-5 h-5 text-cyan1 mb-2" />
          <div className="font-heading text-3xl font-black">{data.stats.comments}</div>
          <div className="text-xs font-mono uppercase tracking-widest text-slate-500">Komentar</div>
        </div>
        <Link to="/bookmarks" className="card p-5 hover:border-cyan1/40 transition" data-testid="stat-bookmarks">
          <Bookmark className="w-5 h-5 text-magenta1 mb-2" />
          <div className="font-heading text-3xl font-black">{data.stats.bookmarks}</div>
          <div className="text-xs font-mono uppercase tracking-widest text-slate-500">Bookmark →</div>
        </Link>
        <Link to="/newsletter" className="card p-5 hover:border-cyan1/40 transition" data-testid="stat-subscription">
          <Mail className="w-5 h-5 text-cyan1 mb-2" />
          <div className="font-heading text-lg font-black flex items-center gap-2">
            {data.subscription.subscribed ? (<><CheckCircle2 className="w-5 h-5 text-green-400" /> Aktif</>) : "Belum"}
          </div>
          <div className="text-xs font-mono uppercase tracking-widest text-slate-500">Newsletter →</div>
        </Link>
      </div>

      {data.subscription.receive_emails !== null && data.subscription.receive_emails !== undefined && (
        <div className="card p-5 mb-10 space-y-4" data-testid="email-pref-card">
          <label className="flex items-center gap-3 cursor-pointer">
            <input type="checkbox" checked={data.subscription.receive_emails} onChange={(e) => toggleEmails(e.target.checked)} className="w-4 h-4 accent-cyan-400" data-testid="email-pref-toggle" />
            <div>
              <div className="text-sm text-slate-200 font-semibold">Terima email digest mingguan &amp; notifikasi berita baru</div>
              <div className="text-xs text-slate-500">Nonaktifkan untuk berhenti menerima email; akses arsip digest tetap aktif.</div>
            </div>
          </label>
          <div className={data.subscription.receive_emails ? "" : "opacity-40 pointer-events-none"}>
            <div className="text-xs font-mono uppercase tracking-widest text-slate-500 mb-2 flex items-center gap-2"><Tags className="w-3 h-3" /> Topik notifikasi berita</div>
            <div className="flex flex-wrap gap-2" data-testid="email-topics">
              {TOPICS.map((t) => {
                const on = (data.subscription.topics || []).includes(t);
                return (
                  <button key={t} type="button" onClick={() => toggleTopic(t)} className={`chip transition ${on ? "chip-cyan" : "opacity-50 hover:opacity-100"}`} data-testid={`topic-${t.toLowerCase()}`} aria-pressed={on}>
                    {on ? <Check className="w-3 h-3" /> : <Plus className="w-3 h-3" />} {t}
                  </button>
                );
              })}
            </div>
            <p className="text-[11px] text-slate-600 mt-2">Digest mingguan tetap mencakup semua topik; pilihan ini berlaku untuk notifikasi berita baru.</p>
          </div>
        </div>
      )}

      <div className="grid lg:grid-cols-2 gap-6 mb-10">
        <form onSubmit={saveName} className="card p-6 space-y-4" data-testid="profile-edit-form">
          <h2 className="font-heading text-xl font-bold flex items-center gap-2"><User className="w-5 h-5 text-cyan1" /> Data Akun</h2>
          <div>
            <label className="text-xs font-mono uppercase tracking-widest text-slate-500">Nama Tampilan</label>
            <input value={name} onChange={(e) => setName(e.target.value)} required className="mt-1 w-full bg-void border border-white/10 rounded-lg h-11 px-3 focus:border-cyan1/50 outline-none" data-testid="profile-name-input" />
          </div>
          <button disabled={busy} className="btn-primary w-full justify-center" data-testid="profile-save-btn">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Simpan
          </button>
        </form>

        <form onSubmit={changePw} className="card p-6 space-y-4" data-testid="profile-password-form">
          <h2 className="font-heading text-xl font-bold flex items-center gap-2"><KeyRound className="w-5 h-5 text-magenta1" /> Ganti Password</h2>
          {isGoogle ? (
            <p className="text-slate-500 text-sm">Akun Anda login via Google — tidak memakai password.</p>
          ) : (
            <>
              <input required type="password" placeholder="Password saat ini" value={pw.current} onChange={(e) => setPw({ ...pw, current: e.target.value })} className="w-full bg-void border border-white/10 rounded-lg h-11 px-3 focus:border-cyan1/50 outline-none" data-testid="profile-current-password" />
              <input required type="password" minLength={8} placeholder="Password baru (min. 8)" value={pw.next} onChange={(e) => setPw({ ...pw, next: e.target.value })} className="w-full bg-void border border-white/10 rounded-lg h-11 px-3 focus:border-cyan1/50 outline-none" data-testid="profile-new-password" />
              <input required type="password" minLength={8} placeholder="Konfirmasi password baru" value={pw.confirm} onChange={(e) => setPw({ ...pw, confirm: e.target.value })} className="w-full bg-void border border-white/10 rounded-lg h-11 px-3 focus:border-cyan1/50 outline-none" data-testid="profile-confirm-password" />
              <button disabled={busy} className="btn-primary w-full justify-center" data-testid="profile-change-password-btn">
                {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <KeyRound className="w-4 h-4" />} Ubah Password
              </button>
            </>
          )}
        </form>
      </div>

      <h2 className="font-heading text-2xl font-bold mb-4 flex items-center gap-2">
        <MessageSquare className="w-5 h-5 text-cyan1" /> Riwayat Komentar ({comments.length})
      </h2>
      {comments.length === 0 ? (
        <p className="text-slate-600 text-sm" data-testid="profile-comments-empty">Belum ada komentar. Mulai diskusi di halaman berita!</p>
      ) : (
        <div className="space-y-3">
          {comments.map((c) => (
            <div key={c.id} className="card p-4 flex gap-3 items-start" data-testid={`profile-comment-${c.id}`}>
              <div className="flex-1 min-w-0">
                <p className="text-sm text-slate-300 break-words">{c.content}</p>
                <div className="flex items-center gap-2 mt-2 text-[11px] font-mono text-slate-600 flex-wrap">
                  {c.article_slug ? (
                    <Link to={`/articles/${c.article_slug}`} className="text-cyan1 hover:underline truncate">{c.article_title}</Link>
                  ) : (
                    <span>{c.article_title}</span>
                  )}
                  <span>· {new Date(c.created_at).toLocaleString("id-ID")}</span>
                  {c.status === "hidden" && <span className="chip chip-magenta text-[10px]">{c.flagged ? "MENUNGGU REVIEW" : "DISEMBUNYIKAN"}</span>}
                </div>
              </div>
              <button onClick={() => deleteComment(c.id)} className="text-slate-600 hover:text-red-400 shrink-0" data-testid={`profile-delete-comment-${c.id}`}>
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
